import { useEffect, useState } from 'react'

import {
  ACCEPTED_CONSENT,
  DEFAULT_CONSENT,
  getConsent,
  hasStoredConsent,
  setConsent,
  type ConsentCategories,
} from '@/lib/cookieConsent'
import { waitForInterFontsReady } from '@/lib/interFontsReady'

export interface CookieConsentBanner {
  open: boolean
  categories: ConsentCategories
  setCategory: (key: keyof ConsentCategories, value: boolean) => void
  onAcceptAll: () => void
  onDenyAll: () => void
  onSavePreferences: () => void
  onDismiss: () => void
  setOpen: (next: boolean) => void
}

export interface CookieConsentBannerOptions {
  /** Hold the first-visit auto-open until true. Default true. */
  autoOpenReady?: boolean
  /** Called after a choice is stored with (previously stored | null, chosen). Not on dismiss. */
  onCommit?: (previous: ConsentCategories | null, next: ConsentCategories) => void
}

/**
 * State for `widgets/CookieBanner`. Knows nothing about auth or analytics — the SPA's
 * `CookieConsentProvider` passes both as options; /mcp-connect passes none. Keep
 * `@/lib/analytics` and `@/context/AuthContext` out of here (the /mcp-connect test guards it).
 */
export function useCookieConsentBanner({
  autoOpenReady = true,
  onCommit,
}: CookieConsentBannerOptions = {}): CookieConsentBanner {
  const [open, setOpen] = useState(false)
  const [categories, setCategories] = useState<ConsentCategories>(() => getConsent() ?? DEFAULT_CONSENT)

  useEffect(() => {
    if (hasStoredConsent()) return undefined
    if (!autoOpenReady) return undefined

    let cancelled = false

    // Open only once Inter is loaded, otherwise the dialog paints in the fallback font and swaps.
    void waitForInterFontsReady().then(() => {
      if (!cancelled) setOpen(true)
    })

    return () => {
      cancelled = true
    }
  }, [autoOpenReady])

  function commit(cats: ConsentCategories) {
    // Diff against storage, not the in-dialog edits.
    const previous = getConsent()
    setConsent(cats)
    setCategories(cats)
    onCommit?.(previous, cats)
    setOpen(false)
  }

  function setCategory(key: keyof ConsentCategories, value: boolean) {
    setCategories(prev => ({ ...prev, [key]: value }))
  }

  function onAcceptAll() {
    commit(ACCEPTED_CONSENT)
  }

  function onDenyAll() {
    commit(DEFAULT_CONSENT)
  }

  function onSavePreferences() {
    commit(categories)
  }

  function onDismiss() {
    setOpen(false)
  }

  return {
    open,
    categories,
    setCategory,
    onAcceptAll,
    onDenyAll,
    onSavePreferences,
    onDismiss,
    setOpen,
  }
}
